import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Info as InfoIcon,
  PersonAdd as PersonAddIcon,
  PersonRemoveAlt1 as RemoveIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { FamilyType } from "@/features/Login/Family/types.ts";
import { useAuthContext } from "@/providers/AuthProvider";
import { useQueryClient } from "@tanstack/react-query";
import {
  FAMILY_KEYS,
  useRemoveFamilyMember,
  useTransferFamilyOwnership,
} from "@/hooks/familyHooks";
import AvatarDisplay from "@/features/Login/Profile/AvatarDisplay.tsx";

interface MembersManagerProps {
  family: FamilyType;
  isOwner: boolean;
}

const MembersManager: React.FC<MembersManagerProps> = ({ family, isOwner }) => {
  const { user } = useAuthContext();
  const [selectedMember, setSelectedMember] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<"remove" | "transfer">(
    "remove"
  );
  const queryClient = useQueryClient();

  // Hooks pour les mutations
  const removeFamilyMemberMutation = useRemoveFamilyMember();
  const transferOwnershipMutation = useTransferFamilyOwnership();

  // Préparer la suppression du membre (sans exécuter immédiatement)
  const prepareRemoveMember = (id: string, name: string) => {
    setSelectedMember({ id, name });
    setDialogAction("remove");
    setConfirmDialogOpen(true);
  };

  // Préparer le transfert de propriété (sans exécuter immédiatement)
  const prepareTransferOwnership = (id: string, name: string) => {
    setSelectedMember({ id, name });
    setDialogAction("transfer");
    setConfirmDialogOpen(true);
  };

  // Confirmer l'action sélectionnée
  const confirmAction = async () => {
    if (!selectedMember) return;

    try {
      if (dialogAction === "remove") {
        // Exécuter la suppression du membre
        await removeFamilyMemberMutation.mutateAsync({
          memberId: selectedMember.id,
          familyId: family._id,
        });
      } else if (dialogAction === "transfer") {
        // Exécuter le transfert de propriété
        await transferOwnershipMutation.mutateAsync({
          newOwnerId: selectedMember.id,
          familyId: family._id,
        });
      }
      // Fermer le dialogue après une action réussie
      setConfirmDialogOpen(false);
      // Actualiser les données de la famille
      queryClient.invalidateQueries({ queryKey: FAMILY_KEYS.details() });
    } catch (error) {
      console.error("Erreur lors de l'action", error);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          Membres de la famille
        </Typography>
        {isOwner && (
          <Tooltip title="Vous pouvez partager le code d'invitation dans l'onglet 'Invitation'">
            <Button
              startIcon={<PersonAddIcon />}
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                /* Naviguer vers l'onglet Invitation */
              }}
            >
              Inviter
            </Button>
          </Tooltip>
        )}
      </Box>

      <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <List disablePadding>
          {family.members.map((member, index) => {
            const isCurrentUser = member._id === user?._id;
            const isFamilyOwner = member._id === family.owner._id;

            return (
              <React.Fragment key={member._id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  sx={{
                    bgcolor: isCurrentUser
                      ? "action.selected"
                      : "background.paper",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <ListItemAvatar>
                    <AvatarDisplay
                      avatarUri={member.avatar}
                      size={40}
                      alt={member.username}
                      fallbackText={member.username}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="body1"
                          fontWeight={isCurrentUser ? "bold" : "normal"}
                        >
                          {member.username}
                          {isCurrentUser && " (Vous)"}
                        </Typography>
                        {isFamilyOwner && (
                          <Tooltip title="Propriétaire de la famille">
                            <StarIcon
                              fontSize="small"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={member.email}
                  />

                  {isOwner && !isCurrentUser && (
                    <ListItemSecondaryAction>
                      <Tooltip title="Transférer la propriété">
                        <IconButton
                          edge="end"
                          onClick={() =>
                            prepareTransferOwnership(
                              member._id,
                              member.username
                            )
                          }
                          sx={{ mr: 1 }}
                        >
                          <StarIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Retirer de la famille">
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() =>
                            prepareRemoveMember(member._id, member.username)
                          }
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      {isOwner && (
        <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
          En tant que propriétaire de la famille, vous pouvez retirer des
          membres ou transférer les droits de propriété.
        </Alert>
      )}

      {/* Dialog de confirmation */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>
          {dialogAction === "remove"
            ? "Retirer un membre"
            : "Transférer la propriété"}
        </DialogTitle>
        <DialogContent>
          {dialogAction === "remove" ? (
            <Typography>
              Êtes-vous sûr de vouloir retirer{" "}
              <strong>{selectedMember?.name}</strong> de votre famille ? Cette
              action ne peut pas être annulée.
            </Typography>
          ) : (
            <Typography>
              Êtes-vous sûr de vouloir transférer la propriété de la famille à{" "}
              <strong>{selectedMember?.name}</strong> ? Vous deviendrez un
              membre normal et ne pourrez plus gérer la famille.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Annuler</Button>
          <Button
            color={dialogAction === "remove" ? "error" : "primary"}
            onClick={confirmAction}
            disabled={
              (dialogAction === "remove" &&
                removeFamilyMemberMutation.isPending) ||
              (dialogAction === "transfer" &&
                transferOwnershipMutation.isPending)
            }
            variant="contained"
          >
            {dialogAction === "remove" ? (
              removeFamilyMemberMutation.isPending ? (
                <CircularProgress size={20} />
              ) : (
                "Retirer"
              )
            ) : transferOwnershipMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Transférer"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MembersManager;
